import {useEffect, useRef, useState} from "react";

import {toast} from "react-toastify";
import styled from "styled-components";
import {scheduleApi} from "../api/schedule-api";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {clearDisplayDeliveriesAction, deliveryState, setDisplayDeliveriesAction,} from "../app/reducers/delivery-slice";
import {clearDateToWorkAction, scheduleState, setSchedulePageByDtoAction,} from "../app/reducers/schedule-slice";
import {SearchUser} from "../components/autocomplete/SearchUser";
import {ScheduleCreateCalendar} from "../components/calendar/ScheduleCreateCalendar";
import {AssignmentMultiSelect, DeliveryAssignmentStatus,} from "../components/selector/AssignmentMultiSelect";
import {ClientFilterMultiSelect} from "../components/selector/ClientMultiSelect";
import {SearchDeliveryTextField} from "../components/text-field/SearchDeliveryTextField";
import {WaitDeliveryList} from "../components/text-field/WaitDeliveryList";
import {DeliveryQueryKey, useGetDeliveryPageAllBySpecificDateQuery} from "../query/delivery-query";
import theme from "../style/theme";
import {Delivery} from "../types/delivery/delivery";
import {DeliveryGroupType} from "../types/schedule/delivery-group-type";
import {ScheduleFormDeliveryView} from "./ScheduleFormDeliveryView";
import {ScheduleFormLineView} from "./ScheduleFormLineView";
import {Company} from "../types/company/company";
import {queryClient} from "../index";
import {profileState} from "../app/reducers/profile-slice";
import useDidMountEffect from "../utils/custom-hook/use-did-mount-effect";

enum ScheduleFormTab {
    DeliveryView = "DELIVERY_VIEW",
    LineView = "LINE_VIEW",
}

export const ScheduleFormView = () => {
    const dispatch = useAppDispatch();
    const {profile} = useAppSelector(profileState);
    const {deliveryPage} = useAppSelector(deliveryState);
    const {updateSchedule, selectedSchedule, dateToWork} =
        useAppSelector(scheduleState);
    const [tab, setTab] = useState<ScheduleFormTab>(ScheduleFormTab.DeliveryView);
    const [checkedClientList, setCheckedClientList] = useState<Company[]>([]);
    const [assignmentStatus, setAssignmentStatus] = useState<DeliveryAssignmentStatus[]>(
        updateSchedule ? [] : [DeliveryAssignmentStatus.BeforeMatched]
    );
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [selectedWorkerId, setSelectedWorkerId] = useState<string | undefined>(
        undefined
    );
    const proRef = useRef<HTMLInputElement>(null);
    const {isFetching} = useGetDeliveryPageAllBySpecificDateQuery({
        specificDate: dateToWork,
        selectedClients: checkedClientList,
        selectedStatus: assignmentStatus,
    });

    const handleClickDeliveryTab = () => {
        setTab(ScheduleFormTab.DeliveryView);
    };

    const handleClickScheduleLineTab = () => {
        setTab(ScheduleFormTab.LineView);
    };

    const handleChangeClientCheckBox = (value: Company) => {
        if (checkedClientList.some((company => company.name === value.name))) {
            const newSelectedClientStatus = checkedClientList.filter(
                (company) => company.name !== value.name
            );
            setCheckedClientList(newSelectedClientStatus);
        } else {
            setCheckedClientList([...checkedClientList, value]);
        }
    };

    const handleFilteredAssignmentStatus = (value: DeliveryAssignmentStatus) => {
        setAssignmentStatus((prevStatus) =>
            prevStatus.includes(value)
                ? prevStatus.filter((status) => status !== value)
                : [...prevStatus, value]
        );
    };

    const getFilteredSearchKeywordDelivery = (
        filteredMatchedDelivery: Delivery[]
    ) => {
        return filteredMatchedDelivery.filter((delivery) => {
            const pickUpWorkPlaceName = delivery.pickUpWorkPlace.name;
            const {road: pickUpRoadAddress, jibun: pickUpJibunAddress} =
                delivery.pickUpLocation;
            const dropOffWorkPlaceName = delivery.dropOffWorkPlace.name;
            const {road: dropOffRoadAddress, jibun: dropOffJibunAddress} =
                delivery.dropOffLocation;

            const stringifyDelivery = JSON.stringify(
                pickUpWorkPlaceName +
                pickUpRoadAddress +
                pickUpJibunAddress +
                dropOffWorkPlaceName +
                dropOffRoadAddress +
                dropOffJibunAddress
            );
            return stringifyDelivery.includes(searchKeyword || "");
        });
    };

    const setDisplayDeliveriesOnDate = (
        filteredSearchKeywordDelivery: Delivery[]
    ) => {
        if (filteredSearchKeywordDelivery.length > 0) {
            const displayPickUpDeliveriesOnDateGrouping =
                filteredSearchKeywordDelivery.reduce((acc: any, delivery) => {
                    acc[delivery.pickUpLocation.id] =
                        acc[delivery.pickUpLocation.id] || [];
                    acc[delivery.pickUpLocation.id].push(delivery);
                    return acc;
                }, {});

            const displayPickUpDeliveriesOnDate = Object.keys(
                displayPickUpDeliveriesOnDateGrouping
            ).map((key) => {
                return {
                    id: key + DeliveryGroupType.PickUp,
                    type: DeliveryGroupType.PickUp,
                    locationId: key,
                    deliveries: displayPickUpDeliveriesOnDateGrouping[key],
                };
            });

            const displayDropOffDeliveriesOnDateGrouping =
                filteredSearchKeywordDelivery.reduce((acc: any, delivery) => {
                    acc[delivery.dropOffLocation.id] =
                        acc[delivery.dropOffLocation.id] || [];
                    acc[delivery.dropOffLocation.id].push(delivery);
                    return acc;
                }, {});

            const displayDropOffDeliveriesOnDate = Object.keys(
                displayDropOffDeliveriesOnDateGrouping
            ).map((key) => {
                return {
                    id: key + DeliveryGroupType.DropOff,
                    type: DeliveryGroupType.DropOff,
                    locationId: key,
                    deliveries: displayDropOffDeliveriesOnDateGrouping[key],
                };
            });

            dispatch(
                setDisplayDeliveriesAction({
                    displayDeliveries: [
                        ...displayPickUpDeliveriesOnDate,
                        ...displayDropOffDeliveriesOnDate,
                    ],
                })
            );
        } else {
            dispatch(clearDisplayDeliveriesAction());
        }
    };

    const handleAssignScheduleButtonClick = async () => {
        if (!selectedSchedule) {
            toast.error("선택된 스케줄이 없습니다.");
            return;
        }

        if (!selectedWorkerId) {
            toast.error("선택된 프로님이 없습니다.");
            return;
        }

        try {
            await scheduleApi.assign(selectedSchedule.id, {
                workerId: selectedWorkerId,
            });
            const response = await scheduleApi.getById(selectedSchedule.id);
            dispatch(setSchedulePageByDtoAction({scheduleDto: response.data}));
        } catch (error) {
            console.log(error);
        }
    };

    const setClientsByNames = (names: string[]) => {
        if (profile) {
            let allClients = profile.clients.concat(profile.company);
            let checkedClients = allClients.filter((client) => names.includes(client.name));
            setCheckedClientList(checkedClients);
        }
    }

    useEffect(() => {
        if (updateSchedule) {
            setClientsByNames(updateSchedule.clientNames)
            setSelectedWorkerId(updateSchedule.worker?.id);
        }
    }, [updateSchedule]);

    useDidMountEffect(async () => {
        if (dateToWork != "") {
            await queryClient.invalidateQueries(DeliveryQueryKey.PageAll);
        }
    }, [checkedClientList, assignmentStatus, dateToWork])

    useEffect(() => {
        const filteredSearchKeywordDelivery = getFilteredSearchKeywordDelivery(deliveryPage.contents);
        setDisplayDeliveriesOnDate(filteredSearchKeywordDelivery);
    }, [deliveryPage, searchKeyword]);

    useEffect(() => {
        return () => {
            dispatch(clearDateToWorkAction());
        };
    }, []);

    return (
        <>
            <ScheduleFormHeader>
                <ScheduleFormHeaderTab
                    active={tab === ScheduleFormTab.DeliveryView}
                    onClick={handleClickDeliveryTab}
                >
                    스케줄 등록
                </ScheduleFormHeaderTab>
                {!updateSchedule && (
                    <ScheduleFormHeaderTab
                        active={tab === ScheduleFormTab.LineView}
                        onClick={handleClickScheduleLineTab}
                    >
                        프로님 배정
                    </ScheduleFormHeaderTab>
                )}
            </ScheduleFormHeader>
            <ScheduleFormSubHeader>
                <ScheduleFormSubHeaderLeft>
                    {tab === ScheduleFormTab.DeliveryView && (
                        <WaitDeliveryList isFetching={isFetching}/>
                    )}
                    <ScheduleCreateCalendar/>
                    <SelectCheckBoxGroupContainer>
                        <LeftSelectCheckBoxGroupContainer>
                            <ClientFilterMultiSelect
                                values={checkedClientList}
                                onClickFilterMultiSelectItem={handleChangeClientCheckBox}
                            />
                        </LeftSelectCheckBoxGroupContainer>
                        <RightSelectCheckBoxGroupContainer>
                            <AssignmentMultiSelect
                                values={assignmentStatus}
                                onClickFilterMultiSelectItem={handleFilteredAssignmentStatus}
                            />
                        </RightSelectCheckBoxGroupContainer>
                    </SelectCheckBoxGroupContainer>

                    <SearchUser
                        userId={selectedWorkerId}
                        updateUserId={(proId?: string) => setSelectedWorkerId(proId)}
                        ref={proRef}
                    />

                    {tab === ScheduleFormTab.LineView && (
                        <AssignButton onClick={handleAssignScheduleButtonClick}>
                            등록하기
                        </AssignButton>
                    )}
                </ScheduleFormSubHeaderLeft>

                {tab === ScheduleFormTab.DeliveryView && (
                    <ScheduleFormSubHeaderRight>
                        <SearchDeliveryLabel>물류 검색</SearchDeliveryLabel>
                        <SearchDeliveryTextField
                            updateSearchDelivery={(newSearchLoad: string) => {
                                setSearchKeyword(newSearchLoad);
                            }}
                        />
                    </ScheduleFormSubHeaderRight>
                )}
            </ScheduleFormSubHeader>
            {tab === ScheduleFormTab.DeliveryView && (
                <ScheduleFormDeliveryView
                    selectedWorkerId={selectedWorkerId}
                    setSelectedWorkerId={setSelectedWorkerId}
                />
            )}
            {tab === ScheduleFormTab.LineView && (
                <ScheduleFormLineView asignmentStatus={assignmentStatus}/>
            )}
        </>
    );
};

const ScheduleFormHeader = styled.div`
  display: flex;
  background: ${theme.palette.primitives.gray900};
  padding: 0 0 20px 24px;
`;

const ScheduleFormSubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid ${theme.palette.primitives.gray200};
`;

const ScheduleFormSubHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ScheduleFormSubHeaderRight = styled.div`
  display: flex;
  align-items: center;
  margin-right: 24px;
  padding-right: 24px;
`;

const AssignButton = styled.button`
  height: 42px;
  background: ${theme.palette.primitives.yellow400};
  border: 1px solid ${theme.palette.primitives.yellow400};
  border-radius: 4px;
  padding: 0 16px;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.black};
  margin-left: 8px;
  cursor: pointer;
`;

interface ScheduleFormHeaderTabProps {
    active: boolean;
}

const ScheduleFormHeaderTab = styled.div<ScheduleFormHeaderTabProps>`
  margin-right: 32px;
  font-weight: ${(props) => (props.active ? 600 : 400)};
  font-size: 20px;
  line-height: 25px;
  color: ${(props) =>
          props.active
                  ? theme.palette.primitives.white
                  : theme.palette.primitives.gray300};
  cursor: pointer;
`;

const SearchDeliveryLabel = styled.div`
  margin-right: 16px;
  font-weight: 600;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.palette.primitives.gray600};
`;

const SelectCheckBoxGroupContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 8px;
  z-index: 999999;
`;

const LeftSelectCheckBoxGroupContainer = styled.div``;

const RightSelectCheckBoxGroupContainer = styled.div`
  margin-left: 8px;
`;
